import { useEffect, useMemo, useState } from 'react';
import { computeFunnel, filtrerCohorte, formaterTaux } from '../../utils/prospectStats';

/**
 * Onglet Stats (§5.8) : le tunnel, **toujours sur une cohorte figée** (`source.runDate`).
 *
 * Sans sélecteur de cohorte, les chiffres bougent tout seuls : un prospect importé en janvier et
 * converti en avril ferait varier « conversions du mois / importés du mois ».
 *
 * À l'étape 1, les barres « Demandes créées » et « Convertis » sont à **0** et leur taux à `—` :
 * c'est le comportement attendu, pas un bug.
 *
 * Étape 2 : « Demandes créées » se remplit pour de vrai, sans retouche ici — la callable
 * `createProspectAccount` écrit `milestones.requestCreatedAt`, que `computeFunnel` lisait déjà.
 * « Convertis (Pro) » reste à 0 jusqu'à l'étape 3 : une demande n'est pas une conversion.
 */

const ETAGES = [
  { cle: 'imported', label: 'Importés', base: 'imported' },
  { cle: 'callable', label: 'Appelables', base: 'imported' },
  { cle: 'contacted', label: 'Contactés', base: 'callable' },
  { cle: 'qualified', label: 'Qualifiés', base: 'contacted' },
  { cle: 'requests', label: 'Demandes créées', base: 'qualified' },
  { cle: 'converted', label: 'Convertis (Pro)', base: 'requests' }
];

const ProspectStats = ({ cohortes = [], tous = [] }) => {
  const [runDate, setRunDate] = useState('');

  // La cohorte la plus récente par défaut, dès qu'elle est connue
  useEffect(() => {
    if (!runDate && cohortes.length > 0) setRunDate(cohortes[0].runDate);
  }, [cohortes, runDate]);

  const cohorte = useMemo(() => filtrerCohorte(tous, runDate), [tous, runDate]);
  const tunnel = useMemo(() => computeFunnel(cohorte), [cohorte]);

  if (tous.length === 0) {
    return (
      <div className="text-center py-12 text-base-content/60">
        <p className="text-lg mb-2">Aucune donnée</p>
        <p className="text-sm">Le tunnel se remplit dès le premier import.</p>
      </div>
    );
  }

  const importes = tunnel.imported || 0;

  return (
    <div>
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <label className="label-text whitespace-nowrap">Cohorte :</label>
        <select
          className="select select-bordered select-sm"
          value={runDate}
          onChange={(evenement) => setRunDate(evenement.target.value)}
        >
          <option value="">Toutes les cohortes</option>
          {cohortes.map((item) => (
            <option key={item.runDate} value={item.runDate}>
              run du {item.runDate} ({item.count})
            </option>
          ))}
        </select>
        <span className="text-sm text-base-content/60">
          {importes} fiche{importes > 1 ? 's' : ''} dans la cohorte
        </span>
      </div>

      <div className="space-y-3">
        {ETAGES.map((etage) => {
          const valeur = tunnel[etage.cle] || 0;
          const base = tunnel[etage.base] || 0;
          const largeur = importes > 0 ? Math.round((valeur / importes) * 100) : 0;
          const tauxEtage =
            etage.cle === 'imported'
              ? null
              : base > 0
              ? Math.round((valeur / base) * 1000) / 10
              : null;

          return (
            <div key={etage.cle} className="flex items-center gap-3">
              <span className="w-40 text-sm text-base-content/70 shrink-0">{etage.label}</span>
              <div className="flex-1 bg-base-200 rounded h-5 overflow-hidden">
                <div
                  className="bg-primary h-full transition-all"
                  style={{ width: `${largeur}%` }}
                  aria-label={`${etage.label} : ${valeur}`}
                />
              </div>
              <span className="w-12 text-right font-semibold">{valeur}</span>
              <span className="w-16 text-right text-sm text-base-content/60">
                {formaterTaux(tauxEtage)}
              </span>
            </div>
          );
        })}
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="stat bg-base-200 rounded-lg">
          <div className="stat-title text-xs">Délai médian demande → Pro</div>
          <div className="stat-value text-lg">
            {tunnel.medianDelayDays === null ? '—' : `${tunnel.medianDelayDays} j`}
          </div>
        </div>
        <div className="stat bg-base-200 rounded-lg">
          <div className="stat-title text-xs">Conversion globale</div>
          <div className="stat-value text-lg">{formaterTaux(tunnel.rates.globalConversionRate)}</div>
        </div>
        <div className="stat bg-base-200 rounded-lg">
          <div className="stat-title text-xs">Hors dénominateur</div>
          <div className="stat-value text-lg">{tunnel.counts.horsDenominateur}</div>
          <div className="stat-desc text-xs">
            {tunnel.counts.excluded} exclus · {tunnel.counts.opposition} oppositions
          </div>
        </div>
      </div>

      <p className="text-xs text-base-content/50 mt-4">
        Une seule source : les jalons de `milestones`. Aucun chiffre n’est calculé depuis
        `calls[]`. « Demandes créées » compte les fiches passées par la modale « Enregistrer la
        demande » (étape 2, jalon `requestCreatedAt`) ; « Convertis (Pro) » reste à 0 jusqu’à
        l’étape 3 : une demande n’est pas une conversion.
      </p>
    </div>
  );
};

export default ProspectStats;
